<template>
  <BaseDialog
    :open="open"
    :disabled="disabled || localLoading"
    max-width="960px"
    body-max-height="80vh"
    @close="emitClose"
  >
    <template #title>
      Schüler versetzen
    </template>

    <!-- Filterleiste -->
    <div
      class="filters"
    >
      <div
        class="field"
      >
        <label for="flt-class-promote">Suche</label>
        <input
          v-model.trim="q"
          placeholder="Name oder Nummer"
          autocomplete="off"
        >
      </div>
      <div
        class="field"
      >
        <label for="flt-class-promote">Klasse</label>
        <select
          id="flt-class-promote"
          v-model="classFilter"
        >
          <option value="">
            Alle
          </option>
          <option
            v-for="c in classOptions"
            :key="'promote-class-'+c.value"
            :value="c.value"
          >
            {{ c.label }}
          </option>
        </select>
      </div>
      <div
        class="field"
      >
        <label for="flt-enroll-promote">Einschulungsjahr</label>
        <select
          id="flt-enroll-promote"
          v-model="enrollFilter"
        >
          <option value="">
            Alle
          </option>
          <option
            v-for="o in enrollmentYearOptions"
            :key="'promote-enroll-'+o.value"
            :value="o.value"
          >
            {{ o.label }}
          </option>
        </select>
      </div>
      <button
        class="btn btn-primary"
        :disabled="!hasFilters"
        @click="resetFilters"
      >
        Reset
      </button>
    </div>

    <div
      class="table-header mt-3 pr-4"
    >
      <div class="text-center">
        <input
          type="checkbox"
          :checked="allSelected"
          :aria-checked="allSelected ? 'true' : 'false'"
          aria-label="Alle Schüler auswählen"
          @change="toggleAll"
        >
      </div>
      <div>Schüler</div>
      <div>Nummer</div>
      <div>Einschulungsjahr</div>
      <div>Aktuelle Klasse</div>
      <div>Versetzung in</div>
    </div>

    <div
      class="rows-container"
      role="region"
      aria-label="Schülerliste"
    >
      <template v-if="filteredItems.length">
        <div
          v-for="p in filteredItems"
          :key="p.id"
          class="table-row"
        >
          <div class="text-center">
            <input
              type="checkbox"
              :checked="!!selected[p.id]"
              :aria-checked="selected[p.id] ? 'true' : 'false'"
              :aria-label="`Schüler auswählen: ${p.last_name}, ${p.first_name}`"
              @change="toggleRow(p.id)"
            >
          </div>

          <div><strong>{{ p.first_name }} {{ p.last_name }}</strong></div>

          <div class="muted">
            {{ p.student_number || '—' }}
          </div>

          <div class="muted">
            {{ enrollmentLabel(p.enrollment_year) }}
          </div>

          <div class="muted">
            {{ p.class_level }}
          </div>

          <div>
            <span
              v-if="String(target[p.id]) === 'ausgeschult'"
              class="help muted"
            >
              Klasse 11 →
            </span>

            <select
              v-model="target[p.id]"
              :disabled="!selected[p.id]"
              :aria-disabled="!selected[p.id] ? 'true' : 'false'"
              :aria-label="`Ziel für Versetzung: ${p.last_name}, ${p.first_name}`"
            >
              <option
                v-for="c in promoteTargetOptionsFor(p)"
                :key="`promote-${p.id}-${c.value}`"
                :value="c.value"
              >
                {{ c.label }}
              </option>
            </select>
          </div>
        </div>
      </template>
      <template v-else>
        <p class="muted">
          Keine Schüler können derzeit versetzt werden.
        </p>
      </template>
    </div>

    <div class="dialog-footer sticky-footer mt-12">
      <button
        class="btn"
        :disabled="disabled || localLoading"
        @click="emitClose"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-primary"
        :disabled="disabled || localLoading || items.length===0 || selectedCount===0"
        @click="handleApply"
      >
        <span
          v-if="localLoading"
          class="spinner"
          aria-hidden="true"
        /> Jetzt versetzen
      </button>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';

const props = defineProps({
  open: {type: Boolean, required: true},
  disabled: {type: Boolean, default: false},
  items: {type: Array, default: () => []},
  info: {type: Object, default: null},
  schoolYears: {type: Array, default: () => []}
});

const emit = defineEmits(['close', 'applied']);

const toast = useToast();

const selected = ref({});
const target = ref({});
const localLoading = ref(false);

// Filterzustände
const q = ref('');
const classFilter = ref('');
const enrollFilter = ref('');

// Optionen
const classOptions = computed(() => {
  return Array.from({length: 10}, (_, i) => {
    const lvl = i + 1;
    return {value: lvl, label: String(lvl)};
  });
});

const enrollmentYearOptions = computed(() => {
  const rows = Array.isArray(props.items) ? props.items : [];
  const yearsFromRows = rows
      .map(p => (p?.enrollment_year ?? '').toString().trim())
      .filter(Boolean);

  const unique = Array.from(new Set(yearsFromRows));

  // Optional: falls ein Code in props.schoolYears vorkommt, nehmen wir dessen "code" als Label
  const syList = Array.isArray(props.schoolYears) ? props.schoolYears : [];
  const labelByCode = new Map(
      syList
          .map(sy => {
            const code = (sy?.code ?? sy?.school_year ?? sy?.name ?? sy?.label ?? sy?.year ?? '').toString().trim();
            return code ? [code, (sy?.code ?? code).toString().trim()] : null;
          })
          .filter(Boolean)
  );

  const parseStartYear = (code) => {
    const m = String(code).match(/^(\d{4})/);
    return m ? Number(m[1]) : -1;
  };

  return unique
      .sort((a, b) => parseStartYear(b) - parseStartYear(a)) // neueste zuerst
      .map(code => ({
        value: code,
        label: labelByCode.get(code) ?? code,
      }));
});

// Gefilterte Items (clientseitig)
const filteredItems = computed(() => {
  const list = Array.isArray(props.items) ? props.items : [];
  const qv = (q.value || '').toString().trim().toLowerCase();
  const classV = classFilter.value !== '' && classFilter.value !== null && classFilter.value !== undefined
      ? Number(classFilter.value)
      : null;
  const enrollV = (enrollFilter.value || '').toString().trim() || null;

  return list.filter(p => {
    if (qv) {
      const last = (p.last_name || '').toString().toLowerCase();
      const first = (p.first_name || '').toString().toLowerCase();
      const num = (p.student_number ?? '').toString().toLowerCase();
      const n1 = `${last} ${first}`.trim();
      const n2 = `${first} ${last}`.trim();
      const n3 = `${last}, ${first}`.trim();
      const hit = n1.includes(qv) || n2.includes(qv) || n3.includes(qv) || num.includes(qv);
      if (!hit) return false;
    }
    if (classV != null) {
      const lvl = Number(p.class_level ?? NaN);
      if (Number.isNaN(lvl) || lvl !== classV) return false;
    }
    if (enrollV != null) {
      const ey = (p.enrollment_year ?? '').toString().trim();
      if (!ey || ey !== enrollV) return false;
    }
    return true;
  });
});

const hasFilters = computed(() => !!(q.value || classFilter.value || enrollFilter.value));

function resetFilters() {
  // Filter leeren
  q.value = '';
  classFilter.value = '';
  enrollFilter.value = '';

  // Alle Checkboxen auf false setzen (für alle Items, nicht nur gefilterte)
  const nextSelected = {...selected.value};
  for (const p of (props.items || [])) {
    nextSelected[p.id] = false;
  }
  selected.value = nextSelected;
}

const selectedCount = computed(() => {
  const ids = (props.items || []).map(p => p.id);
  return ids.reduce((acc, id) => acc + (selected.value[id] ? 1 : 0), 0);
});

const allSelected = computed(() => {
  const list = filteredItems.value;
  if (!list || list.length === 0) return false;
  return list.every(p => !!selected.value[p.id]);
});

function initState() {
  const sel = {};
  const tgt = {};
  for (const p of (props.items || [])) {
    sel[p.id] = false;
    tgt[p.id] = defaultPromotionTargetFor(p);
  }
  selected.value = sel;
  target.value = tgt;
}

watch(() => props.items, () => {
  initState();
}, {immediate: true});

// Beim Öffnen: Filter leeren und vollständigen Reset inkl. Targets
watch(() => props.open, (val) => {
  if (val) {
    resetFilters(); // leert Filter und setzt alle Checkboxen auf false
    initState();    // stellt Default-Ziele wieder her und setzt Selektionen auf false
  }
});

function emitClose() {
  emit('close');
}

function toggleRow(studentId) {
  selected.value[studentId] = !selected.value[studentId];
  if (selected.value[studentId] && target.value[studentId] == null) {
    const p = (props.items || []).find(x => x.id === studentId);
    target.value[studentId] = defaultPromotionTargetFor(p);
  }
}

function toggleAll(e) {
  const checked = !!e?.target?.checked;
  const nextSelected = {...selected.value};
  const nextTarget = {...target.value};

  for (const p of (filteredItems.value || [])) {
    nextSelected[p.id] = checked;
    if (checked && nextTarget[p.id] == null) {
      nextTarget[p.id] = defaultPromotionTargetFor(p);
    }
  }

  selected.value = nextSelected;
  target.value = nextTarget;
}

function defaultPromotionTargetFor(p) {
  if (!p) return 1;
  const currentLevel = Number(p.class_level ?? 0);
  const nextLevel = currentLevel + 1;

  if (nextLevel > 10) return 'ausgeschult';
  if (nextLevel >= 1 && nextLevel <= 10) return nextLevel;

  return 1;
}

function promoteTargetOptionsFor(p) {
  const base = Array.from({length: 10}, (_, i) => {
    const lvl = i + 1;
    return {value: lvl, label: String(lvl)};
  });

  const currentLevel = Number(p?.class_level ?? 0);
  if ((currentLevel + 1) > 10) {
    base.push({value: 'ausgeschult', label: 'ausgeschult'});
  }

  return base;
}

function enrollmentLabel(enrollmentYearCode) {
  const s = (enrollmentYearCode ?? '').toString().trim();
  return s || '—';
}

async function handleApply() {
  if (!props.info?.schoolYearId) {
    toast.add({severity: 'danger', summary: 'Kein Schuljahr gewählt.', life: 3000});
    return;
  }

  const toApply = (props.items || [])
      .filter(p => !!selected.value[p.id])
      .map(p => ({
        student_id: p.id,
        target: target.value[p.id] ?? defaultPromotionTargetFor(p),
      }));

  if (toApply.length === 0) {
    toast.add({severity: 'info', summary: 'Bitte mindestens einen Schüler auswählen.', life: 2500});
    return;
  }

  localLoading.value = true;
  try {
    await api.post('/students/promotions', {
      school_year_id: props.info.schoolYearId,
      items: toApply,
    });
    toast.add({severity: 'success', summary: 'Versetzung durchgeführt.', life: 3500});
    emit('applied');
    emitClose();
  } catch (e) {
    const msg = e?.response?.data?.error?.message || 'Versetzung fehlgeschlagen.';
    toast.add({severity: 'danger', summary: msg, life: 3500});
  } finally {
    localLoading.value = false;
  }
}
</script>

<style scoped>
.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  background: var(--surface);
  border-top: 1px solid var(--border);
}

.rows-container {
  height: 360px; /* feste Höhe für scrollbare Zeilen */
  overflow-y: auto;
  padding-right: 4px; /* kleiner Puffer für Scrollbar */
}

.filters {
  display: grid;
  align-items: end;
  gap: 8px;
  grid-template-columns: 1fr 120px 140px 70px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.filters .field {
  margin-bottom: 0;
}

.filters label {
  margin-bottom: 2px;
}

.table-header, .table-row {
  grid-template-columns: 44px 1fr 80px 120px 140px 220px;
}

.table-row {
  padding: 4px 0;
}

.table-row select {
  text-align: center;
  min-width: 50px;
  padding: 0 10px;
  height: 28px;
}

:deep(footer) {
  display: none;
}
</style>
